import { onCLS, onINP, onLCP, onTTFB, Metric } from "web-vitals";

// CWV describe a document lifetime, including interactions after SPA navigation.
// Keep the entry-page cohort fixed; do not mislabel these as SPA route timings.
export function rumPageGroup(pathname: string): string {
  if (pathname === "/") return "home";
  if (/^\/sunglasses\/?$/.test(pathname)) return "sunglasses";
  if (/^\/eyeglasses\/?$/.test(pathname)) return "eyeglasses";
  if (/^\/(?:store\/)?(?:cart|checkout)(?:\/|$)/.test(pathname))
    return "checkout";
  if (/^\/account(?:\/|$)/.test(pathname)) return "account";
  if (document.querySelector('[data-page-kind="product"]')) return "product";
  if (document.querySelector('[data-page-kind="catalog"]')) return "catalog";
  return "other";
}

export function installWebVitals(): void {
  if (typeof window === "undefined") return;
  const pending = new Map<string, Record<string, unknown>>();
  const page = rumPageGroup(window.location.pathname);
  const device = /mobile|android|iphone|ipad/i.test(navigator.userAgent)
    ? "mobile"
    : "desktop";
  const browser = /Edg\//.test(navigator.userAgent)
    ? "edge"
    : /Firefox|FxiOS/.test(navigator.userAgent)
    ? "firefox"
    : /Chrome|CriOS/.test(navigator.userAgent)
    ? "chrome"
    : /Safari/.test(navigator.userAgent)
    ? "safari"
    : "other";
  let installed = false;
  let visitor = "unknown";
  let timer = 0;
  let sent = 0;
  const allowed = () => window.vfConsent?.has("analytics") === true;
  const flush = () => {
    window.clearTimeout(timer);
    timer = 0;
    if (!allowed()) {
      pending.clear();
      return;
    }
    if (!pending.size || sent >= 40) return;
    const body = JSON.stringify({ version: 1, metrics: [...pending.values()] });
    pending.clear();
    sent++;
    try {
      if (
        navigator.sendBeacon?.(
          "/api/rum",
          new Blob([body], { type: "text/plain" })
        )
      )
        return;
      void fetch("/api/rum", {
        method: "POST",
        body,
        keepalive: true,
        credentials: "omit",
        headers: { "Content-Type": "text/plain" },
      }).catch(() => undefined);
    } catch {
      // Telemetry must never interrupt shopping; no retries on unload.
    }
  };
  const report = (metric: Metric) => {
    if (!allowed() || sent >= 40) return;
    pending.set(metric.name, {
      id: metric.id,
      name: metric.name,
      value: metric.value,
      page,
      device,
      browser,
      visitor,
      marketing: window.vfConsent?.has("marketing") ? "granted" : "denied",
      navigation: metric.navigationType,
    });
    // web-vitals reports final values when hidden. Flush from a microtask so
    // every observer handling that visibility event can finish first.
    if (document.visibilityState === "hidden") queueMicrotask(flush);
    else if (!timer) timer = window.setTimeout(flush, 10000);
  };
  const start = () => {
    if (!allowed()) {
      pending.clear();
      window.clearTimeout(timer);
      timer = 0;
      return;
    }
    if (installed) return;
    installed = true;
    try {
      visitor =
        localStorage.getItem("vf_rum_seen") === "1" ? "returning" : "new";
      localStorage.setItem("vf_rum_seen", "1");
    } catch {
      /* Storage is optional; use the unknown cohort. */
    }
    const options = { reportAllChanges: true };
    onCLS(report, options);
    onINP(report, options);
    onLCP(report, options);
    onTTFB(report);
  };
  window.addEventListener("vf:consent-updated", start);
  window.addEventListener("pagehide", flush);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") queueMicrotask(flush);
  });
  start();
}
